import React, { useRef, useEffect, useLayoutEffect, useMemo, useCallback, useState } from 'react';
import MessageBubble from './MessageBubble';
import DateSeparator from './DateSeparator';
import { groupMessagesByDate } from '../../utils/dateUtils';

/**
 * Message list component with date grouping + scroll-to-top pagination
 */
const MessageList = ({
    conversationId,
    messages,
    isLoading,
    onForward,
    onReact,
    onDelete,
    onReply,
    onEdit,
    onSchedule,
    onPhoneClick,
    onVerifyPayment,
    verifyingMessageIds = {},
    // Older-message pagination props
    onLoadOlder,
    hasMoreOlder,
    isLoadingOlder,
    isForwardSelectionMode = false,
    selectedMessagesToForward = [],
    onToggleMessageSelection
}) => {
    const listRef = useRef(null);
    const messagesEndRef = useRef(null);
    const prevMessageCountRef = useRef(0);
    const prevScrollHeightRef = useRef(0);
    const isInitialLoadRef = useRef(true);
    const activeConvIdRef = useRef(conversationId);
    const scrollThrottleRef = useRef(null);

    // Group messages by date
    const groupedMessages = useMemo(() => {
        if (!messages || !Array.isArray(messages)) return {};
        return groupMessagesByDate(messages);
    }, [messages]);

    // Get sorted date keys
    const sortedDateKeys = useMemo(() => {
        return Object.keys(groupedMessages).sort((a, b) => {
            if (a === 'unknown') return -1;
            if (b === 'unknown') return 1;
            return new Date(a) - new Date(b);
        });
    }, [groupedMessages]);

    // --- SCROLL MANAGEMENT ---
    
    // To maintain scroll position when prepending messages, we use useLayoutEffect
    const [highlightedId, setHighlightedId] = useState(null);
    const [previewQuote, setPreviewQuote] = useState(null);
    const isAddingOlderRef = useRef(false);

    // Helper to scroll to bottom reliably
    const scrollToBottom = useCallback((behavior = 'auto') => {
        const list = listRef.current;
        if (!list) return;
        list.scrollTop = list.scrollHeight;
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
        }
    }, []);

    // Detect when messages are prepended (length increase)
    useEffect(() => {
        if (isLoadingOlder) {
            isAddingOlderRef.current = true;
        }
    }, [isLoadingOlder]);

    // Reset initial load state when conversation ID changes
    useEffect(() => {
        if (conversationId !== activeConvIdRef.current) {
            activeConvIdRef.current = conversationId;
            isInitialLoadRef.current = true;
            prevMessageCountRef.current = 0;
            prevScrollHeightRef.current = 0;
        }
    }, [conversationId]);

    // Reset initial load status when the message list becomes empty
    useEffect(() => {
        if (!messages || messages.length === 0) {
            isInitialLoadRef.current = true;
            prevMessageCountRef.current = 0;
            prevScrollHeightRef.current = 0;
        }
    }, [messages]);

    useLayoutEffect(() => {
        const list = listRef.current;
        if (!list || !messages || messages.length === 0 || isLoading) return;

        // Initial load for conversation: scroll to bottom
        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            prevScrollHeightRef.current = list.scrollHeight;
            prevMessageCountRef.current = messages.length;

            scrollToBottom('auto');

            // Re-scroll after layout paint and image render cycles
            const t1 = setTimeout(() => scrollToBottom('auto'), 50);
            const t2 = setTimeout(() => scrollToBottom('auto'), 150);
            const t3 = setTimeout(() => scrollToBottom('auto'), 350);

            return () => {
                clearTimeout(t1);
                clearTimeout(t2);
                clearTimeout(t3);
            };
        }

        const currentCount = messages.length;
        const newScrollHeight = list.scrollHeight;
        const countDiff = currentCount - prevMessageCountRef.current;
        const heightDiff = newScrollHeight - prevScrollHeightRef.current;

        // Case 1: Older messages loaded (prepended)
        if (countDiff > 0 && isAddingOlderRef.current) {
            list.scrollTop = list.scrollTop + heightDiff;
            isAddingOlderRef.current = false;
        } 
        // Case 2: New messages arrived (appended)
        else if (countDiff > 0) {
            const isNearBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 350;
            const lastMsg = messages[messages.length - 1];
            const wasSentByMe = lastMsg?.sender === 'agent' || lastMsg?.agentId || lastMsg?.sender_type === 'bot';

            if (isNearBottom || wasSentByMe) {
                scrollToBottom('smooth');
            }
        }

        prevScrollHeightRef.current = newScrollHeight;
        prevMessageCountRef.current = currentCount;
    }, [messages, isLoading, isLoadingOlder, scrollToBottom]);

    const handleScroll = useCallback(() => {
        // Throttle scroll handler to max once per 150ms to prevent perf issues
        if (scrollThrottleRef.current) return;
        scrollThrottleRef.current = setTimeout(() => {
            scrollThrottleRef.current = null;
            const list = listRef.current;
            if (!list || !onLoadOlder || !hasMoreOlder || isLoadingOlder) return;
            if (list.scrollTop < 80) {
                prevScrollHeightRef.current = list.scrollHeight;
                onLoadOlder();
            }
        }, 150);
    }, [onLoadOlder, hasMoreOlder, isLoadingOlder]);

    // Handle clicking a quote to scroll to original message
    const handleQuoteClick = useCallback((replyTo) => {
        if (!replyTo || !replyTo.id) return;
        
        // Clean ID (remove @s.whatsapp.net suffix and trim)
        const rawId = String(replyTo.id).split('@')[0].trim();
        
        console.log(`🎯 Attempting to scroll to message: ${rawId}`);
        
        // 1. Direct DOM lookup
        let element = document.getElementById(`msg-${rawId}`);
        
        // 2. Search in state with flexible matching (handling both WA ID and DB ID)
        if (!element) {
            const foundMessage = messages.find(m => {
                const waId = m.whatsapp_id ? String(m.whatsapp_id).split('@')[0].trim() : null;
                const dbId = m.id ? String(m.id).trim() : null;
                return waId === rawId || dbId === rawId;
            });
            
            if (foundMessage) {
                const targetId = String(foundMessage.whatsapp_id || foundMessage.id).split('@')[0].trim();
                element = document.getElementById(`msg-${targetId}`);
                console.log(`📍 Found in state. Target DOM ID: msg-${targetId}`);
            }
        }
        
        if (element && listRef.current) {
            console.log('✨ Message element found! Scrolling...');
            
            // Highlight original message ID
            const elementMsgId = element.id.replace('msg-', '');
            setHighlightedId(elementMsgId);

            // Robust scroll logic
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Clear highlight after animation
            setTimeout(() => {
                setHighlightedId(null);
            }, 2500);
        } else {
            console.log(`❌ Message ${rawId} not found in DOM or state. Showing modal.`);
            setPreviewQuote(replyTo);
        }
    }, [messages]);

    if (isLoading) {
        return (
            <div className="chat-messages flex-center" style={{ height: '100%' }}>
                <div className="loading">
                    <p style={{ color: 'var(--color-gray-500)' }}>Cargando mensajes...</p>
                </div>
            </div>
        );
    }

    if (!messages || messages.length === 0) {
        return (
            <div className="chat-messages flex-center" style={{ height: '100%' }}>
                <div style={{ textAlign: 'center', color: 'var(--color-gray-500)' }}>
                    <p>No hay mensajes en esta conversación</p>
                    <p style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-2)' }}>
                        Envía un mensaje para iniciar
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="chat-messages"
            ref={listRef}
            onScroll={handleScroll}
            style={{ willChange: 'transform' }}
        >
            {/* Load-older indicator at the top */}
            {isLoadingOlder && (
                <div style={{
                    textAlign: 'center',
                    padding: '8px',
                    color: 'var(--color-gray-500)',
                    fontSize: 'var(--font-size-sm)'
                }}>
                    <span>Cargando mensajes anteriores...</span>
                </div>
            )}
            {!hasMoreOlder && messages?.length > 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '6px',
                    color: 'var(--color-gray-400)',
                    fontSize: 'var(--font-size-xs)'
                }}>
                    Inicio de la conversación
                </div>
            )}

            {sortedDateKeys.map(dateKey => {
                const group = groupedMessages[dateKey];
                return (
                    <div key={dateKey} className="message-group">
                        <DateSeparator label={group.label} />
                        {group.messages.map((message, index) => {
                            // Clean the ID for matching (important for WhatsApp suffixes)
                            const msgId = String(message.whatsapp_id || message.id).split('@')[0].trim();
                            return (
                                <div 
                                    key={msgId || `${dateKey}-${index}`}
                                    id={`msg-${msgId}`}
                                    className={highlightedId === msgId ? 'message-highlight' : ''}
                                    style={{ borderRadius: '8px', transition: 'transform 0.3s ease' }}
                                >
                                    <MessageBubble
                                        message={message}
                                        onForward={onForward}
                                        onReact={onReact}
                                        onDelete={onDelete}
                                        onReply={onReply}
                                        onEdit={onEdit}
                                        onSchedule={onSchedule}
                                        onPhoneClick={onPhoneClick}
                                        onQuoteClick={handleQuoteClick}
                                        onVerifyPayment={onVerifyPayment}
                                        isVerifying={!!verifyingMessageIds[msgId]}
                                        isForwardSelectionMode={isForwardSelectionMode}
                                        isSelectedForForward={selectedMessagesToForward.some(m => m.id === message.id)}
                                        onToggleMessageSelection={onToggleMessageSelection}
                                    />
                                </div>
                            );
                        })}
                    </div>
                );
            })}
            <div ref={messagesEndRef} />

            {/* Floating Quote Preview Modal */}
            {previewQuote && (
                <div 
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        backdropFilter: 'blur(2px)',
                        padding: '20px'
                    }}
                    onClick={() => setPreviewQuote(null)}
                >
                    <div 
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            padding: '20px',
                            maxWidth: '400px',
                            width: '100%',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                            animation: 'slideUp 0.3s ease-out',
                            position: 'relative'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '12px',
                            borderBottom: '1px solid var(--color-gray-100)',
                            paddingBottom: '8px'
                        }}>
                            <span style={{ 
                                fontWeight: '600', 
                                color: 'var(--color-primary)',
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                📝 Mensaje Original
                            </span>
                            <button 
                                onClick={() => setPreviewQuote(null)}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    color: 'var(--color-gray-400)'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div style={{ 
                            fontSize: 'var(--font-size-md)',
                            lineHeight: '1.5',
                            color: 'var(--color-gray-800)',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap'
                        }}>
                            <div style={{ 
                                fontSize: 'var(--font-size-xs)', 
                                color: 'var(--color-gray-500)',
                                marginBottom: '4px',
                                fontWeight: '500'
                            }}>
                                {previewQuote.sender || 'Usuario'} escribió:
                            </div>
                            {previewQuote.text || 'Archivo multimedia'}
                        </div>
                        
                        <div style={{ marginTop: '16px', textAlign: 'right' }}>
                            <button 
                                className="btn btn-primary"
                                style={{ padding: '6px 16px', fontSize: '13px' }}
                                onClick={() => setPreviewQuote(null)}
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessageList;
